/**
 * iter701 — Scan invoice QR → create Expense / Supplier record.
 *
 * Workflow:
 *   1. Open the camera (rear by default; toggleable).
 *   2. Continuously decode QR codes with `jsqr` at ~10 fps.
 *   3. The first valid hit is parsed via `parseInvoiceQR` (ZATCA TLV →
 *      seller name, VAT number, timestamp, total, VAT amount; falls
 *      back gracefully for non-Saudi QRs).
 *   4. A side panel pre-fills the Save form with the parsed fields;
 *      the cashier picks Branch + Payment Mode + (optional) Supplier
 *      and saves to `/expenses`.
 *
 * Why not server-side OCR? The QR is the supplier-side cryptographic
 * commitment to the line totals — by law (ZATCA Phase 2) it cannot be
 * altered without invalidating the cryptographic stamp. Trusting the QR
 * means we capture exactly what the vendor billed, no OCR drift.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import jsQR from 'jsqr';
import { toast } from 'sonner';
import { VatTreatmentFields, vatPayload, vatError } from '@/components/VatTreatmentFields';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Camera, CheckCircle2, Loader2, ScanLine, RefreshCcw, Info, ChevronLeft, AlertTriangle,
} from 'lucide-react';
import api from '@/lib/api';
import { useBranchStore } from '@/stores/branchStore';
import { parseInvoiceQR } from '@/lib/zatcaQrParser';

const EXPENSE_CATEGORIES = [
  'Food & Beverage', 'Office Supplies', 'Utilities', 'Rent', 'Travel',
  'Transportation', 'Equipment', 'Marketing', 'Professional Services',
  'Repairs & Maintenance', 'Cleaning', 'Other',
];

const PAYMENT_MODES = [
  { id: 'cash',   label: 'Cash' },
  { id: 'bank',   label: 'Bank / Card' },
  { id: 'credit', label: 'Credit (pay later)' },
];

export default function QRInvoiceScanPage() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const [cameraOn, setCameraOn] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [scanned, setScanned] = useState(null); // { ok, fields, raw, format }
  const [saving, setSaving] = useState(false);
  const { branches, fetchBranches } = useBranchStore();
  const [suppliers, setSuppliers] = useState([]);
  const [form, setForm] = useState({
    category: 'Other',
    description: '',
    amount: 0,
    vat_amount: 0,
    vat_inclusive: true,
    payment_mode: 'cash',
    branch_id: '',
    supplier_id: '',
    invoice_number: '',
    date: new Date().toISOString().slice(0, 10),
    notes: '',
  });
  const [showHelp, setShowHelp] = useState(false);
  // iter819 — Live duplicate guard for scanned expenses. Auto-extracted bills
  // are a common source of silent doubles (scanning the same receipt twice, or
  // a bill already keyed in manually), so we check as soon as the parsed
  // amount + branch + date are set.
  const [dupWarn, setDupWarn] = useState(null);
  // iter749 — Scanned seller → supplier matching. Holds the seller info
  // parsed from the ZATCA QR + whether we auto-linked an existing supplier.
  const [supplierMatch, setSupplierMatch] = useState(null); // { sellerName, vatNumber, matched, creating }

  // Match a scanned seller to an existing supplier: VAT number first
  // (exact, most reliable), then case-insensitive name / alt-name.
  const findSupplierMatch = (sellerName, vatNumber, list) => {
    const sups = Array.isArray(list) ? list : suppliers;
    const vat = (vatNumber || '').trim();
    if (vat) {
      const byVat = sups.find((s) => (s.vat_number || '').trim() === vat);
      if (byVat) return byVat;
    }
    const name = (sellerName || '').trim().toLowerCase();
    if (name) {
      return sups.find((s) => {
        const n = (s.name || '').trim().toLowerCase();
        if (n && (n === name || n.includes(name) || name.includes(n))) return true;
        return (s.alt_names || []).some((a) => (a || '').trim().toLowerCase() === name);
      });
    }
    return null;
  };

  // ── Load branches + suppliers once ────────────────────────────────
  useEffect(() => {
    fetchBranches?.();
    (async () => {
      try {
        const { data } = await api.get('/suppliers');
        setSuppliers(Array.isArray(data) ? data : []);
      } catch { /* non-critical */ }
    })();
  }, [fetchBranches]);

  // ── Start the camera ─────────────────────────────────────────────
  const startCamera = async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraOn(true);
      // Kick off the decode loop.
      tick();
    } catch (e) {
      setCameraError(e?.message || 'Camera permission denied. Allow camera access in your browser settings.');
    }
  };

  const stopCamera = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCameraOn(false);
  };

  // Decode loop. Runs while cameraOn AND scanned === null. Throttles to
  // ~10 FPS by skipping frames between RAFs.
  const tick = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState !== video.HAVE_ENOUGH_DATA) {
      rafRef.current = requestAnimationFrame(tick);
      return;
    }
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const code = jsQR(img.data, img.width, img.height, { inversionAttempts: 'dontInvert' });
    if (code?.data) {
      const parsed = parseInvoiceQR(code.data);
      setScanned(parsed);
      stopCamera();
      // Pre-fill the form with parsed data.
      const total = parsed.fields?.invoice_total_num || 0;
      const vat = parsed.fields?.vat_amount_num || 0;
      const sellerName = parsed.fields?.seller_name || '';
      const vatNumber = parsed.fields?.vat_number || '';
      // iter749 — Auto-link the scanned seller to an existing supplier.
      const matched = findSupplierMatch(sellerName, vatNumber, suppliers);
      setSupplierMatch({ sellerName, vatNumber, matched: !!matched, creating: false });
      setForm((f) => ({
        ...f,
        supplier_id: matched ? matched.id : f.supplier_id,
        description: sellerName ? `Invoice from ${sellerName}` : (f.description || 'Scanned invoice'),
        amount: total || f.amount,
        vat_amount: vat || f.vat_amount,
        vat_treatment: vat > 0 ? 'standard' : f.vat_treatment,
        supplier_vat_number: vatNumber || f.supplier_vat_number,
        date: (parsed.fields?.invoice_timestamp || '').slice(0, 10) || f.date,
        notes: [
          sellerName && `Seller: ${sellerName}`,
          vatNumber && `VAT #: ${vatNumber}`,
          parsed.format === 'unknown' && `Raw QR (non-ZATCA): ${parsed.raw.slice(0, 80)}`,
        ].filter(Boolean).join('\n'),
      }));
      if (matched) {
        toast.success(`Linked to supplier: ${matched.name} ✓`);
      } else {
        toast.success(parsed.format === 'zatca' ? 'ZATCA QR scanned ✓' : 'QR scanned — non-ZATCA, fill remaining fields.');
      }
      return;
    }
    rafRef.current = requestAnimationFrame(tick);
  };

  // Tear down camera on unmount.
  useEffect(() => () => stopCamera(), []);

  // iter819 — Debounced duplicate check against existing expenses.
  useEffect(() => {
    const amt = parseFloat(form.amount);
    if (!scanned || !form.branch_id || !form.date || !Number.isFinite(amt) || amt <= 0) { setDupWarn(null); return; }
    const timer = setTimeout(async () => {
      try {
        const p = new URLSearchParams({
          branch_id: form.branch_id, amount: String(amt), date: form.date,
          category: form.category || '', payment_mode: form.payment_mode || '',
          invoice_number: form.invoice_number || '', supplier_id: form.supplier_id || '',
        });
        const r = await api.get(`/expenses/check-duplicate?${p.toString()}`);
        setDupWarn(r.data || null);
      } catch { setDupWarn(null); }
    }, 500);
    return () => clearTimeout(timer);
  }, [scanned, form.amount, form.branch_id, form.date, form.category, form.payment_mode, form.invoice_number, form.supplier_id]);

  // ── Reset to scan again ──────────────────────────────────────────
  const handleRescan = () => {
    setScanned(null);
    setSupplierMatch(null);
    setForm({
      category: 'Other', description: '', amount: 0, vat_amount: 0,
      vat_inclusive: true, payment_mode: 'cash', branch_id: form.branch_id,
      supplier_id: '', invoice_number: '', date: new Date().toISOString().slice(0, 10), notes: '',
    });
    startCamera();
  };

  // iter749 — Create a supplier on the fly from the scanned seller info
  // (name + VAT number) and link it to this expense. Saves the cashier
  // from leaving the scanner to create the supplier manually.
  const handleCreateSupplierFromScan = async () => {
    if (!supplierMatch?.sellerName) { toast.error('No seller name in the scanned QR.'); return; }
    setSupplierMatch((m) => ({ ...m, creating: true }));
    try {
      const { data: sup } = await api.post('/suppliers', {
        name: supplierMatch.sellerName,
        vat_number: supplierMatch.vatNumber || null,
        branch_id: form.branch_id || null,
        category: 'Supplier (scanned)',
      });
      setSuppliers((prev) => [...prev, sup]);
      setForm((f) => ({ ...f, supplier_id: sup.id }));
      setSupplierMatch((m) => ({ ...m, matched: true, creating: false }));
      toast.success(`Supplier "${sup.name}" created & linked ✓`);
    } catch (e) {
      setSupplierMatch((m) => ({ ...m, creating: false }));
      toast.error(e?.response?.data?.detail || 'Failed to create supplier');
    }
  };

  // ── Save to /expenses ────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.description.trim()) { toast.error('Description required'); return; }
    if (!form.amount || form.amount <= 0) { toast.error('Amount must be greater than zero'); return; }
    if (!form.branch_id) { toast.error('Pick a branch'); return; }
    const _ve = vatError(form);
    if (_ve) { toast.error(_ve); return; }
    setSaving(true);
    try {
      const vatRate = form.amount > 0 && form.vat_amount > 0
        ? +(form.vat_amount / (form.amount - form.vat_amount) * 100).toFixed(2)
        : 0;
      const payload = {
        category: form.category,
        description: form.description,
        amount: form.amount,
        vat_amount: form.vat_amount || 0,
        vat_rate: vatRate,
        vat_inclusive: form.vat_inclusive,
        payment_mode: form.payment_mode,
        branch_id: form.branch_id || null,
        supplier_id: form.supplier_id || null,
        invoice_number: form.invoice_number || null,
        date: new Date(form.date + 'T00:00:00Z').toISOString(),
        notes: form.notes || null,
        ...vatPayload(form),
      };
      const { data } = await api.post('/expenses', payload);
      toast.success(`Expense saved · ${data.invoice_number || data.id?.slice(0, 8)}`);
      navigate('/expenses');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save expense');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-4" data-testid="qr-scan-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="qr-back-btn">
              <ChevronLeft size={16} className="mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2"><ScanLine size={22} className="text-primary" /> Scan invoice QR</h1>
              <p className="text-xs text-muted-foreground">Capture supplier bills straight from their printed QR code · ZATCA Phase 1 & 2 supported</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setShowHelp(true)} data-testid="qr-help-btn">
            <Info size={14} className="mr-1" /> Help
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Scanner column */}
          <Card data-testid="qr-scanner-card">
            <CardContent className="p-4 space-y-3">
              <div className="relative aspect-[4/3] bg-zinc-900 rounded-xl overflow-hidden flex items-center justify-center">
                <video ref={videoRef} playsInline muted className={`w-full h-full object-cover ${cameraOn ? '' : 'hidden'}`} data-testid="qr-video" />
                <canvas ref={canvasRef} className="hidden" />
                {!cameraOn && !cameraError && (
                  <div className="text-center text-white/80 p-6">
                    <Camera size={42} className="mx-auto mb-2 opacity-70" />
                    <p className="text-sm">Tap <strong>Start camera</strong> to begin scanning.</p>
                  </div>
                )}
                {cameraError && (
                  <div className="text-center text-rose-200 p-6">
                    <p className="text-xs">{cameraError}</p>
                  </div>
                )}
                {cameraOn && (
                  <div className="absolute inset-6 border-2 border-white/40 rounded-2xl pointer-events-none">
                    <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-emerald-400 rounded-tl-2xl" />
                    <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-emerald-400 rounded-tr-2xl" />
                    <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-emerald-400 rounded-bl-2xl" />
                    <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-emerald-400 rounded-br-2xl" />
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                {!cameraOn ? (
                  <Button onClick={startCamera} className="flex-1 bg-primary hover:bg-primary/90 text-white" data-testid="qr-start-camera">
                    <Camera size={14} className="mr-1.5" /> Start camera
                  </Button>
                ) : (
                  <Button variant="outline" onClick={stopCamera} className="flex-1" data-testid="qr-stop-camera">
                    Stop camera
                  </Button>
                )}
                {scanned && (
                  <Button variant="outline" onClick={handleRescan} data-testid="qr-rescan">
                    <RefreshCcw size={14} className="mr-1.5" /> Rescan
                  </Button>
                )}
              </div>
              {scanned && (
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-xs space-y-1" data-testid="qr-result">
                  <p className="font-semibold text-emerald-900 flex items-center gap-1.5">
                    <CheckCircle2 size={14} /> {scanned.format === 'zatca' ? 'ZATCA QR detected' : 'Non-ZATCA QR — manual fields needed'}
                  </p>
                  {scanned.fields?.seller_name && <p><strong>Seller:</strong> {scanned.fields.seller_name}</p>}
                  {scanned.fields?.vat_number && <p><strong>VAT #:</strong> {scanned.fields.vat_number}</p>}
                  {scanned.fields?.invoice_timestamp && <p><strong>Date:</strong> {scanned.fields.invoice_timestamp}</p>}
                  {scanned.fields?.invoice_total_num != null && <p><strong>Total:</strong> {scanned.fields.invoice_total_num.toFixed(2)}</p>}
                  {scanned.fields?.vat_amount_num != null && <p><strong>VAT:</strong> {scanned.fields.vat_amount_num.toFixed(2)}</p>}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Save form column */}
          <Card data-testid="qr-save-form">
            <CardContent className="p-4 space-y-3">
              <h2 className="font-semibold text-base flex items-center gap-2">
                Save as expense
              </h2>
              <div>
                <Label className="text-xs">Description *</Label>
                <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="h-9" data-testid="qr-form-description" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Total (incl. VAT)</Label>
                  <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })} className="h-9" data-testid="qr-form-amount" />
                </div>
                <div>
                  <Label className="text-xs">VAT amount</Label>
                  <Input type="number" step="0.01" value={form.vat_amount} onChange={(e) => setForm({ ...form, vat_amount: parseFloat(e.target.value) || 0 })} className="h-9" data-testid="qr-form-vat" />
                </div>
              </div>
              <VatTreatmentFields value={form} amount={form.amount} onChange={(p) => setForm((f) => ({ ...f, ...p }))} withInvoice={false} testPrefix="qr-vat" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="h-9" data-testid="qr-form-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {EXPENSE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Date</Label>
                  <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-9" data-testid="qr-form-date" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Branch *</Label>
                  <Select value={form.branch_id || 'none'} onValueChange={(v) => setForm({ ...form, branch_id: v === 'none' ? '' : v })}>
                    <SelectTrigger className="h-9" data-testid="qr-form-branch"><SelectValue placeholder="Select branch" /></SelectTrigger>
                    <SelectContent>
                      {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Payment method</Label>
                  <Select value={form.payment_mode} onValueChange={(v) => setForm({ ...form, payment_mode: v })}>
                    <SelectTrigger className="h-9" data-testid="qr-form-payment-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PAYMENT_MODES.map((m) => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {/* iter749 — Scanned-supplier match banner. Shows the seller
                  parsed from the QR and whether we auto-linked it. */}
              {supplierMatch && (supplierMatch.sellerName || supplierMatch.vatNumber) && (
                <div
                  className={`rounded-lg border p-2.5 text-xs ${supplierMatch.matched ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50'}`}
                  data-testid="qr-supplier-match"
                >
                  <div className="font-medium text-stone-800">
                    Scanned seller: {supplierMatch.sellerName || '(no name)'}
                    {supplierMatch.vatNumber && <span className="text-stone-500"> · VAT {supplierMatch.vatNumber}</span>}
                  </div>
                  {supplierMatch.matched ? (
                    <div className="text-emerald-700 mt-0.5 flex items-center gap-1" data-testid="qr-supplier-linked">
                      <CheckCircle2 size={13} /> Linked to supplier below ✓
                    </div>
                  ) : (
                    <div className="mt-1.5 flex items-center justify-between gap-2">
                      <span className="text-amber-700">No matching supplier found.</span>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-[11px] border-amber-400 text-amber-800 hover:bg-amber-100"
                        disabled={supplierMatch.creating || !supplierMatch.sellerName}
                        onClick={handleCreateSupplierFromScan}
                        data-testid="qr-create-supplier"
                      >
                        {supplierMatch.creating && <Loader2 size={12} className="animate-spin mr-1" />}
                        Create supplier "{(supplierMatch.sellerName || '').slice(0, 18)}"
                      </Button>
                    </div>
                  )}
                </div>
              )}
              {/* Supplier link is OPTIONAL — only mandatory when payment_mode is credit. */}
              <div>
                <Label className="text-xs">Supplier {form.payment_mode === 'credit' && <span className="text-rose-600">*</span>}</Label>
                <Select value={form.supplier_id || 'none'} onValueChange={(v) => setForm({ ...form, supplier_id: v === 'none' ? '' : v })}>
                  <SelectTrigger className="h-9" data-testid="qr-form-supplier"><SelectValue placeholder="(not linked)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">(not linked)</SelectItem>
                    {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Invoice number</Label>
                <Input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} className="h-9" placeholder="(optional)" data-testid="qr-form-invoice-no" />
              </div>
              <div>
                <Label className="text-xs">Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="text-xs" data-testid="qr-form-notes" />
              </div>
              {/* iter819 — Live duplicate warning for scanned bills */}
              {dupWarn && (dupWarn.count > 0 || dupWarn.cross_category_count > 0) && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 flex items-start gap-2" data-testid="qr-dup-warning">
                  <AlertTriangle size={15} className="text-amber-600 mt-0.5 shrink-0" />
                  <div className="text-xs text-amber-800">
                    {dupWarn.count > 0
                      ? <>A <b>same-category</b> expense of this amount at this branch already exists on {form.date}. This scan may be a duplicate.</>
                      : <>A same-day expense of <b>this amount at this branch</b> already exists{dupWarn.cross_category_existing?.[0]?.category ? <> under <b>{dupWarn.cross_category_existing[0].category}</b></> : null} on {form.date}. This scan may be a duplicate.</>}
                    {([...(dupWarn.existing || []), ...(dupWarn.cross_category_existing || [])].length > 0) && (
                      <span className="block text-[11px] text-amber-700 mt-0.5">
                        Existing: {[...(dupWarn.existing || []), ...(dupWarn.cross_category_existing || [])].slice(0, 3).map(e => `${e.category || '—'}${e.payment_mode ? ' (' + e.payment_mode + ')' : ''}`).join(' · ')}
                      </span>
                    )}
                  </div>
                </div>
              )}
              <Button
                className="w-full bg-primary hover:bg-primary/90 text-white"
                disabled={saving || !scanned}
                onClick={handleSave}
                data-testid="qr-save-expense"
              >
                {saving && <Loader2 size={14} className="animate-spin mr-1.5" />}
                {scanned ? 'Save expense' : 'Scan a QR first'}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Help modal */}
        {showHelp && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6" onClick={() => setShowHelp(false)}>
            <div className="bg-white rounded-2xl max-w-md p-6 space-y-3 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="qr-help-modal">
              <h2 className="font-bold text-lg flex items-center gap-2"><Info size={18} className="text-primary" /> How to use</h2>
              <ol className="list-decimal ml-5 text-sm space-y-1.5 leading-relaxed">
                <li>Tap <strong>Start camera</strong> and grant access if prompted.</li>
                <li>Point the rear camera at the supplier&apos;s invoice QR — the green corners snap to the code automatically.</li>
                <li><strong>ZATCA QRs (Saudi Arabia)</strong> auto-fill the seller, VAT number, total, VAT amount, and date.</li>
                <li>For non-ZATCA QRs you&apos;ll see the raw payload — fill in the missing fields manually.</li>
                <li>Pick <strong>Branch</strong>, <strong>Payment method</strong> (Cash / Bank / Credit), and (optional) <strong>Supplier</strong>. Credit must be linked to a supplier so accounts payable stays accurate.</li>
                <li>Tap <strong>Save expense</strong> — the bill posts to the same `/expenses` ledger your manual entries use, with full audit trail.</li>
              </ol>
              <p className="text-xs text-muted-foreground">
                Camera processing happens entirely on this device. The QR payload is only sent to your Smarto backend if you actually save the expense.
              </p>
              <div className="flex justify-end">
                <Button size="sm" onClick={() => setShowHelp(false)} data-testid="qr-help-close">Got it</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
